import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { formatBytes, formatDate, formatDuration } from '../lib/audio';
import { formatModelLabel, SUMMARY_MODELS } from '../lib/models';

const OLD_MEETINGS_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const OLD_MEETINGS_BATCH_SIZE = 25;

interface Props {
  onSelect: (meetingId: Id<'meetings'>) => void;
}

export function HistoryList({ onSelect }: Props) {
  const meetings = useQuery(api.meetings.list, {});
  const removeMeeting = useMutation(api.meetings.remove);
  const removeOldMeetings = useMutation(api.meetings.removeOld);
  const retryMeeting = useMutation(api.meetings.retry);
  const regenerateSummary = useMutation(api.meetings.regenerateSummary);
  const [summaryModelByMeeting, setSummaryModelByMeeting] = useState<Record<string, string>>({});
  const [busyMeetingId, setBusyMeetingId] = useState<string | null>(null);
  const [purgingOld, setPurgingOld] = useState(false);
  const [search, setSearch] = useState('');

  const onDelete = async (id: Id<'meetings'>, title: string) => {
    if (!window.confirm(`¿Borrar "${title}"? Esta acción no se puede deshacer.`)) return;
    await removeMeeting({ meetingId: id });
  };

  const onRetry = async (id: Id<'meetings'>) => {
    await retryMeeting({ meetingId: id });
  };

  const onPurgeOld = async () => {
    if (!window.confirm('¿Borrar reuniones con más de 30 días? Esta acción no se puede deshacer.')) return;
    setPurgingOld(true);
    try {
      let totalDeleted = 0;
      let hasMore = true;
      while (hasMore) {
        const result = await removeOldMeetings({
          olderThanMs: OLD_MEETINGS_AGE_MS,
          limit: OLD_MEETINGS_BATCH_SIZE,
        });
        totalDeleted += result.deletedCount;
        hasMore = result.hasMore && result.deletedCount > 0;
      }
      window.alert(`Reuniones antiguas eliminadas: ${totalDeleted}`);
    } finally {
      setPurgingOld(false);
    }
  };

  const onRegenerateSummary = async (meeting: NonNullable<typeof meetings>[number]) => {
    const summaryModel = summaryModelByMeeting[meeting._id] ?? meeting.summaryModel;
    if (!window.confirm(`¿Regenerar el summary de "${meeting.title || meeting.fileName}" con ${formatModelLabel(summaryModel)}?`)) return;
    setBusyMeetingId(meeting._id);
    try {
      await regenerateSummary({ meetingId: meeting._id, summaryModel });
    } finally {
      setBusyMeetingId(null);
    }
  };

  if (meetings === undefined) {
    return (
      <section className="reveal-up">
        <p className="label-mono mb-4">
          <span className="text-[var(--color-amber-mid)]">[02]</span> · ls --history --reverse
        </p>
        <div className="term-box p-8 text-center relative z-10">
          <div className="spinner-tech mx-auto mb-3" />
          <p className="font-mono text-xs text-[var(--color-asphalt-500)]">$ fetching…</p>
        </div>
      </section>
    );
  }

  if (meetings.length === 0) {
    return (
      <section className="reveal-up">
        <p className="label-mono mb-4">
          <span className="text-[var(--color-amber-mid)]">[02]</span> · ls --history --reverse
        </p>
        <div className="term-box p-12 text-center relative z-10">
          <pre className="font-mono text-[var(--color-asphalt-400)] text-xs mb-4 select-none">{`╔════════════════════╗
║   NO_RECORDS_YET   ║
╚════════════════════╝`}</pre>
          <p className="font-mono text-sm text-[var(--color-asphalt-500)]">
            <span className="text-[var(--color-amber-mid)]">$</span> sube tu primer audio en{' '}
            <span className="text-[var(--color-asphalt)]">[new_meeting]</span>
          </p>
        </div>
      </section>
    );
  }

  const query = search.trim().toLowerCase();
  const filtered = query
    ? meetings.filter(
        (m) =>
          (m.title ?? '').toLowerCase().includes(query) ||
          m.fileName.toLowerCase().includes(query)
      )
    : meetings;

  return (
    <section className="reveal-up">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <p className="label-mono">
          <span className="text-[var(--color-amber-mid)]">[02]</span> · ls --history --reverse
        </p>
        <div className="flex items-center gap-3 flex-wrap justify-end no-print">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-asphalt-500)]">
            <span className="nums-tabular text-[var(--color-asphalt)] glow-text">
              {String(filtered.length).padStart(2, '0')}
            </span>
            {query ? `/${String(meetings.length).padStart(2, '0')}` : ''} records
          </p>
          <button
            type="button"
            onClick={onPurgeOld}
            disabled={purgingOld}
            className="term-btn"
            title="Borra reuniones con más de 30 días"
          >
            {purgingOld ? '$ purging…' : '$ rm --old 30d'}
          </button>
        </div>
      </div>

      {meetings.length > 3 && (
        <div className="mb-4 no-print">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="$ grep -i 'título o archivo'…"
            aria-label="Buscar en el historial por título o nombre de archivo"
            className="term-input"
          />
        </div>
      )}

      {filtered.length === 0 && (
        <div className="term-box p-8 text-center relative z-10">
          <p className="font-mono text-xs text-[var(--color-asphalt-500)]">
            <span className="text-[var(--color-amber-mid)]">$ grep:</span> sin coincidencias para "{search.trim()}"
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {filtered.map((m) => {
          const canRegenerateSummary =
            m.chunksCompleted === m.chunkCount &&
            m.chunkCount > 0 &&
            m.status !== 'pending' &&
            m.status !== 'transcribing' &&
            m.status !== 'summarizing';
          const selectedSummaryModel = summaryModelByMeeting[m._id] ?? m.summaryModel;
          const isBusy = busyMeetingId === m._id;

          return (
          <li key={m._id}>
            <article
              className={[
                'group term-box term-box-hover',
                'transition-all cursor-pointer relative z-10',
              ].join(' ')}
            >
              <button
                type="button"
                onClick={() => onSelect(m._id)}
                className="block w-full text-left p-5"
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
                  <h3 className="heading-display text-xl text-[var(--color-asphalt)]">
                    {m.title || m.fileName}
                  </h3>
                  <StatusPill
                    status={m.status}
                    chunksCompleted={m.chunksCompleted}
                    chunkCount={m.chunkCount}
                    transcriptionModel={m.transcriptionModel}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[11px] text-[var(--color-asphalt-400)] nums-tabular">
                  <span>{formatDate(m._creationTime)}</span>
                  <span className="text-[var(--color-asphalt-300)]">│</span>
                  <span className="truncate max-w-xs">{m.fileName}</span>
                  <span className="text-[var(--color-asphalt-300)]">│</span>
                  <span>{formatBytes(m.fileSizeBytes)}</span>
                  {m.durationSec != null && (
                    <>
                      <span className="text-[var(--color-asphalt-300)]">│</span>
                      <span>{formatDuration(m.durationSec)}</span>
                    </>
                  )}
                  {m.chunkCount > 1 && (
                    <>
                      <span className="text-[var(--color-asphalt-300)]">│</span>
                      <span>{m.chunkCount} chunks</span>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="term-chip">
                    stt <b>{formatModelLabel(m.transcriptionModel)}</b>
                  </span>
                  <span className="term-chip">
                    sum <b>{formatModelLabel(m.summaryModel)}</b>
                  </span>
                </div>

                {m.error && (
                  <p className="font-mono text-[11px] text-[var(--color-burgundy)] mt-3 line-clamp-2">
                    [stderr] {m.error}
                  </p>
                )}
              </button>

              <div className="px-5 pb-3 flex justify-end items-center gap-4 flex-wrap no-print">
                {canRegenerateSummary && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={selectedSummaryModel}
                      onChange={(event) =>
                        setSummaryModelByMeeting((current) => ({
                          ...current,
                          [m._id]: event.target.value,
                        }))
                      }
                      disabled={isBusy}
                      className="history-model-select"
                      aria-label="Modelo para reprocesar summary"
                    >
                      {SUMMARY_MODELS.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => onRegenerateSummary(m)}
                      disabled={isBusy}
                      className="term-link text-[10px]"
                    >
                      {isBusy ? '$ summary…' : '$ summary --rerun'}
                    </button>
                  </div>
                )}
                {m.status !== 'done' && (
                  <button
                    type="button"
                    onClick={() => onRetry(m._id)}
                    className="term-link text-[10px]"
                  >
                    $ retry
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(m._id, m.title || m.fileName)}
                  className="term-link term-link-danger text-[10px]"
                >
                  $ rm -f
                </button>
              </div>
            </article>
          </li>
          );
        })}
      </ul>
    </section>
  );
}

function StatusPill({
  status,
  chunksCompleted,
  chunkCount,
  transcriptionModel,
}: {
  status: 'pending' | 'transcribing' | 'summarizing' | 'done' | 'error';
  chunksCompleted: number;
  chunkCount: number;
  transcriptionModel: string;
}) {
  if (status === 'done') return <span className="pill pill-done">✓ ok</span>;
  if (status === 'error') return <span className="pill pill-error">✗ fail</span>;
  if (status === 'pending') return <span className="pill pill-pending">queued</span>;
  if (status === 'transcribing') {
    return (
      <span className="pill pill-active">
        <div className="spinner-tech" style={{ width: 8, height: 8, borderWidth: 1 }} />
        {formatModelLabel(transcriptionModel)} {chunksCompleted}/{chunkCount}
      </span>
    );
  }
  if (status === 'summarizing') {
    return (
      <span className="pill pill-active">
        <div className="spinner-tech" style={{ width: 8, height: 8, borderWidth: 1 }} />
        summarizing
      </span>
    );
  }
  return null;
}
